import Skeleton from '@mui/material/Skeleton';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import React, { useEffect } from 'react';

import Link from '@/components/Link';

interface DashboardCardProps {
  title: string;
  /**
   * Suppresses the visible chrome title span — for cards that render their own
   * heading inside `children` (e.g. Account Overview's AccountRangeHeader),
   * avoiding a duplicate "Account Overview". The header row collapses entirely
   * when no other chrome (badge, headerRight, link, collapse button) is present.
   * `data-card-title` is retained for identity.
   */
  hideTitle?: boolean;
  badge?: number;
  attentionBadge?: React.ReactNode;
  headerRight?: React.ReactNode;
  collapseButton?: React.ReactNode;
  collapsed?: boolean;
  linkText?: string;
  linkTo?: string;
  loading?: boolean;
  error?: boolean;
  /** Underlying error, reported to Sentry when the error branch renders. */
  errorDetail?: unknown;
  /** When provided, the error branch shows a retry button wired to this. */
  onRetry?: () => void;
  fullWidth?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function DashboardCard({
  title,
  hideTitle,
  badge,
  attentionBadge,
  headerRight,
  collapseButton,
  collapsed,
  linkText,
  linkTo,
  loading,
  error,
  errorDetail,
  onRetry,
  fullWidth,
  className,
  children,
}: DashboardCardProps): React.ReactNode {
  const showError = !!error && !loading;
  useEffect(() => {
    if (showError && errorDetail) Sentry.captureException(errorDetail);
  }, [showError, errorDetail]);

  const hasHeaderChrome =
    !hideTitle ||
    collapseButton !== undefined ||
    (badge !== undefined && badge > 0) ||
    attentionBadge !== undefined ||
    headerRight !== undefined ||
    (linkText !== undefined && linkTo !== undefined);
  return (
    <div
      data-testid="dashboard-card"
      data-card-title={title}
      className={clsx(
        'rounded-md border border-surface-border bg-surface-card p-20 transition-shadow hover:shadow-1',
        fullWidth && 'col-span-full',
        className
      )}
    >
      {hasHeaderChrome && (
        <div className="mb-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            {collapseButton}
            {!hideTitle && (
              <span className="text-14 font-semibold leading-snug text-on-surface">{title}</span>
            )}
            {badge !== undefined && badge > 0 && (
              <span className="inline-flex h-20 min-w-[2rem] items-center justify-center rounded-full bg-brand-light px-6 text-10 font-semibold text-brand-primary">
                {badge}
              </span>
            )}
            {attentionBadge}
          </div>
          <div className="flex items-center gap-8">
            {headerRight}
            {linkText && linkTo && (
              <Link to={linkTo} className="text-12 font-medium text-brand-primary">
                {linkText} &rarr;
              </Link>
            )}
          </div>
        </div>
      )}
      {!collapsed &&
        (showError ? (
          <div className="flex items-center gap-8">
            <p className="text-12 text-status-error">Couldn&apos;t load data.</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="text-12 font-medium text-brand-primary underline-offset-2 hover:underline"
              >
                Retry
              </button>
            )}
          </div>
        ) : loading ? (
          <div className="flex flex-col gap-8">
            <Skeleton variant="rounded" height={20} sx={{ bgcolor: 'var(--surfaceMuted)' }} />
            <Skeleton variant="rounded" height={40} sx={{ bgcolor: 'var(--surfaceMuted)' }} />
            <Skeleton
              variant="rounded"
              height={20}
              width="60%"
              sx={{ bgcolor: 'var(--surfaceMuted)' }}
            />
          </div>
        ) : (
          children
        ))}
    </div>
  );
}
